/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import counterpart from 'counterpart';
import { produce } from 'immer';
import OrgSelectorWidget from 'Containers/OrgSelectorWidget/OrgSelectorWidget';
import 'Containers/DataAccessFilter/DataAccessFilter.scss';
import { OrgTypes, getOrgName, getAncestorHierarchyCode, getOrgLabelFromHierarchyCode } from 'Utils/organizationUtils';
import { getAcquirerAccessList } from 'Utils/permissionsUtils';

class OrgHierarchySelectorRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      orgState: props.initialSelections,
      selectedOrgIds: [],
      selectedOrgTypes: [],
      rowKey: props.rowKey,
      isLocked: false,
      isValid: false,
      isMidSearchLocked: false,
      allOption: { label: 'All', mainLine: 'All', id: 'All', hierarchyCode: 'All' },
      SYSTEM_CORP_COUNT: 19,
      EXTERNAL_ROLES: ['MERCHANT_ACCOUNT_ADMINISTRATOR', 'MERCHANT_USER'],
      orgTypeStatusMap: null,
    };
    this.attachBindings();
  }

  componentDidMount() {
    this.onInitializeRow();
  }

  // initialize the row status when first loading
  onInitializeRow() {
    this.lookupOrganizations()
    .then(corps => {
      this.updateSelectionStatus();
      this.updateTierItems(OrgTypes.CORP, corps);
      this.handleHierarchyCallback();
    });
  }

  // called when user selects an organization from the dropdown list for any of the tiers in the hierarchy (other than merchant)
  onOrgSelected(selectedHierarchyCode, currentTierType) {
    // do nothing if they reselect the same item already selected
    const prevSelectedItem = this.getSelectedItem(currentTierType);
    if (prevSelectedItem && prevSelectedItem.hierarchyCode === selectedHierarchyCode) return;
    this.clearAndResetDescendants(currentTierType);

    if (selectedHierarchyCode === 'Select') {
      this.updateCurrentTierSelection(currentTierType, null);
      this.updateSelectionStatus();
      this.handleHierarchyCallback();
    } else {
      const nextTierType = currentTierType + 1;
      const itemToSelect = this.findItemToSelect(currentTierType, selectedHierarchyCode);
      this.updateCurrentTierSelection(currentTierType, itemToSelect);
      this.updateSelectionStatus();
      this.handleHierarchyCallback();
      this.lookupOrganizations(nextTierType, itemToSelect.orgId, itemToSelect.hierarchyCode)
        .then(childOrgs => {
          this.updateTierItems(nextTierType, childOrgs);
          if (currentTierType === OrgTypes.CHAIN) this.handleHierarchyCallback();
        });
    }
  }

  // handler called when the user selects or deselects a merchant in the merchant list
  onMerchantSelected(selectionId) {
    this.toggleMerchantItem(selectionId);
    this.updateSelectionStatus();
    this.handleHierarchyCallback();
  }

  // handler called when user initiates a search by FULL mid to select a hierarchy starting with MID instead of drilling down from corp
  onLazySearchByMID(MID) {
    const trimmedMid = MID.replace(/(^[0 ]+)|(^\s+|\s+$)/g, '');
    return this.lookupOrganizations(OrgTypes.MERCHANT, null, trimmedMid)
      .then(midResult => {
        const foundMid = midResult && midResult.find((x) => x.merchantNumber === trimmedMid);
        if (foundMid) {
          this.updateAncestorSelections(foundMid);
          this.updateSelectionStatus();
          this.onLazyLoad(OrgTypes.MERCHANT);
          this.handleHierarchyCallback();
          return '';
        } else {
          this.props.onSelectMerchant(MID); 
          let acquirerAccessList= getAcquirerAccessList();
          return  acquirerAccessList.includes("HPY") ? '' : 'MID not found';
        }
      });
  }

  // called when a user manually clicks to expand a selector that has a selected value but no populated list items yet (such as when editing data access)
  onLazyLoad(tierType) {
    const prevTierSelection = this.state.orgState[tierType - 1] && this.state.orgState[tierType - 1].selectedItems[0];
    this.lookupOrganizations(tierType, prevTierSelection?prevTierSelection.orgId:undefined, prevTierSelection?prevTierSelection.hierarchyCode:undefined)
      .then(orgs => {
        this.updateTierItems(tierType, orgs);
        this.handleHierarchyCallback();
      });
  }

  // return the selected item
  getSelectedItem(tierType) {
    return this.state.orgState[tierType].selectedItems[0];
  }

  // find the last selected item in this row (i.e., left most column not set to All/Select )
  getLastSelectedItem(state) {
    const columns = [...state.orgState].reverse();
    const col = columns.find(x => x.selectedItems.length > 0 && (x.selectedItems[0].id !== 'Select' || x.selectedItems[0].id !== 'All'));
    return col;
  }

  // determine whether to show 'All' or 'Select' caption at a given tier of the hierarchy
  getAllOrSelectCaption(state, tierType) {
    let prevTierSelection;
    let hasParentTierAccess = false;
    let disallowAllMerchantsForChain000 = false;
    if (tierType === OrgTypes.CORP) {
      hasParentTierAccess = false;
    } else {
      prevTierSelection = state.orgState[tierType - 1].selectedItems[0];
      if (!prevTierSelection) {
        hasParentTierAccess = true;
      } else {
        hasParentTierAccess = prevTierSelection.hasAccess;
      }
      disallowAllMerchantsForChain000 = (tierType === OrgTypes.MERCHANT &&
                                          prevTierSelection && prevTierSelection.mainLine === '000' &&
                                          this.state.EXTERNAL_ROLES.includes(this.props.targetRole));
    }
    return (hasParentTierAccess || !this.props.targetRole) && !disallowAllMerchantsForChain000 ? this.props.selectorConfig[getOrgName(tierType)].defaultsTo : 'Select';
  }

  // helper to toggle UI to loading state, fetch requested child organizations, and then toggle loading on completion
  lookupOrganizations(tierType, orgId, hierarchyCode) {
    if (tierType) this.toggleLoading(tierType);
    return this.props.lookupOrganizations(orgId, hierarchyCode)
      .then((orgs) => {
        if (tierType) this.toggleLoading(tierType);
        return orgs;
      });
  }

  // determine whether the user must fill in a selection to pass row validation
  isSelectionRequired(state, tierType) {
    return this.getAllOrSelectCaption(state, tierType) === 'Select';
  }

  // find an item to select based on id (hierarchyCode)
  findItemToSelect(tierType, id) {
    return this.state.orgState[tierType].items.find(i => i.id === id);
  }

  // trigger the callback to notify parent component of change to selected hierarchy
  handleHierarchyCallback() {
    let state;
    // call setState to ensure callback fires in order it was called after any other batched setState calls
    this.setState(prevState => {
      state = prevState;
      return { ...prevState };
    }, () => this.props.hierarchySelectionCallback(state.rowKey, state.selectedOrgIds, state.selectedOrgTypes, state.orgState, state.isValid, state.orgTypeStatusMap.rowErrorCount));
  }

  // toggle merchant item between selected/unselect
  toggleMerchantItem(selectionId) {
    this.setState(produce(draft => {
      const merchantTier = draft.orgState[OrgTypes.MERCHANT];
      if (selectionId === 'All') {
        const merchantItem = this.findItemToSelect(OrgTypes.MERCHANT, selectionId);
        const selected = merchantTier.selectedItems.find(i => i.id === merchantItem.id);
        merchantTier.selectedItems = merchantTier.selectedItems.filter(x => x.id == 'All');
        if (selected) {
          merchantTier.selectedItems = [];
        } else {
          merchantTier.selectedItems.push(merchantItem);
        }
      } else {
        const merchantItem = this.findItemToSelect(OrgTypes.MERCHANT, selectionId);
        const selected = merchantTier.selectedItems.find(i => i.id === merchantItem.id);
        merchantTier.selectedItems = merchantTier.selectedItems.filter(x => x.id !== 'All');
        if (selected) {
          merchantTier.selectedItems = merchantTier.selectedItems.filter(x => x.id !== selected.id);
        } else {
          merchantTier.selectedItems.push(merchantItem);
        }
      }
    }));
  }

  // update the ancestor selections in the hierarchy when starting with full MID search instead of drilling down from corp
  updateAncestorSelections(foundMid) {
    this.setState(produce(draft => {
      if (this.props.isGlobalSelector) draft.isMidSearchLocked = true;
      Object.values(OrgTypes).forEach((orgType) => {
        const hierarchyCode = getAncestorHierarchyCode(orgType, foundMid.parentHierarchyCode);
        const parentHierarchyCode = (orgType > 0) ? getAncestorHierarchyCode(hierarchyCode) : null;
        const name = getOrgLabelFromHierarchyCode(orgType, hierarchyCode);
        draft.orgState[orgType].isValid = true;
        draft.orgState[orgType].selectedItems = [].concat({
          hierarchyCode,
          parentHierarchyCode,
          id: hierarchyCode,
          orgId: (this.props.isGlobalSelector && orgType === OrgTypes.CHAIN && foundMid.parentOrganizationId) ? foundMid.parentOrganizationId : null,
          name,
          mainLine: name,
          subLine: '',
        });
      });
      const merchantTier = draft.orgState[OrgTypes.MERCHANT];
      merchantTier.selectedItems = [foundMid];
    }));
  }

  // update the selected item at the given tier
  updateCurrentTierSelection(tierType, itemToSelect) {
    this.setState(produce(draft => {
      const currentTierState = draft.orgState[tierType];
      currentTierState.selectedItems = itemToSelect ? [itemToSelect] : [];
    }));
  }

  // update the available list of organizations within a given tier
  updateTierItems(tierType, orgs) {
    let hierarchyCode;
    this.setState(produce(draft => {
      const tierState = draft.orgState[tierType];
      const selectOrAll = this.getAllOrSelectCaption(draft, tierType);
      tierState.items = (selectOrAll === 'Select') ? orgs : [].concat(this.state.allOption, orgs);
      if (tierState.items && tierState.items.length === 1) {
        hierarchyCode = orgs[0].hierarchyCode;
      }
    }), () => {
      if (hierarchyCode) {
        this.onOrgSelected(hierarchyCode, tierType);
      }
    });
  }

  // returns an object with orgType status to help with error notification
  buildOrgStatusMap(state) {
    const selectedRequiredMap = {};
    state.orgState.forEach(tier => {
      selectedRequiredMap[tier.orgType] = {
        required: this.isSelectionRequired(state, tier.orgType),
        selected: tier.selectedItems.length > 0,
      };
    });
    selectedRequiredMap.rowErrorCount = Object.values(selectedRequiredMap).filter(orgStatus => orgStatus.required && !orgStatus.selected).length;
    return selectedRequiredMap;
  }

  // updates misc calculated states for the row based on current selections
  updateSelectionStatus() {
    this.setState(produce(draft => {
      draft.isValid = !draft.orgState.some(tier => this.isSelectionRequired(draft, tier.orgType) && tier.selectedItems.length === 0);
      const currentSelection = [...draft.orgState].reverse().find(o => o.selectedItems.length > 0);
      let currentOrgIds = currentSelection ? currentSelection.selectedItems.filter(o => o.orgId).map(o => o.orgId) : [];
      const currentOrgTypes = currentSelection ? currentSelection.selectedItems.filter(o => o.orgType > -1).map(o => o.orgType) : [];
      if (this.props.isGlobalSelector && !currentOrgIds.length && currentSelection && currentSelection.selectedItems.length) {
        // user does not have direct access to the current organization selected, so get the highest
        // tier organization(s) they have direct access to that are descendants of the current selection
        // const selectedHierarchyCode = currentSelection.selectedItems[0].hierarchyCode;
        currentOrgIds = []; // this.props.getDirectTopLevelOrganizations(selectedHierarchyCode).map(x => x.orgId);
      }
      draft.selectedOrgIds = draft.isValid ? currentOrgIds : [];
      draft.selectedOrgTypes = draft.isValid ? currentOrgTypes : [];
      draft.orgTypeStatusMap = this.buildOrgStatusMap(draft);
    }));
  }

  // helper to toggle loading status of a dropdown as well as lock the row
  toggleLoading(tierType) {
    this.setState(produce(draft => {
      const isLoading = !draft.orgState[tierType].isLoading;
      draft.orgState[tierType].isLoading = isLoading;
      draft.isLocked = isLoading;
    }));
  }

  // parent org selection was changed, so clear and reset descendant tiers
  clearAndResetDescendants(parentOrgType) {
    this.setState(produce(draft => {
      for (let i = parentOrgType + 1; i <= OrgTypes.MERCHANT; i += 1) {
        const org = draft.orgState[i];
        org.items = [];
        org.selectedItems = [];
      }
    }));
  }

  attachBindings() {
    this.onOrgSelected = this.onOrgSelected.bind(this);
    this.onMerchantSelected = this.onMerchantSelected.bind(this);
    this.clearAndResetDescendants = this.clearAndResetDescendants.bind(this);
    this.onInitializeRow = this.onInitializeRow.bind(this);
    this.onLazyLoad = this.onLazyLoad.bind(this);
    this.onLazySearchByMID = this.onLazySearchByMID.bind(this);
    this.buildOrgStatusMap = this.buildOrgStatusMap.bind(this);
    this.getSelectedItem = this.getSelectedItem.bind(this);
    this.getAllOrSelectCaption = this.getAllOrSelectCaption.bind(this);
    this.getLastSelectedItem = this.getLastSelectedItem.bind(this);
    this.isSelectionRequired = this.isSelectionRequired.bind(this);
    this.findItemToSelect = this.findItemToSelect.bind(this);
    this.lookupOrganizations = this.lookupOrganizations.bind(this);
    this.toggleLoading = this.toggleLoading.bind(this);
    this.toggleMerchantItem = this.toggleMerchantItem.bind(this);
    this.updateAncestorSelections = this.updateAncestorSelections.bind(this);
    this.updateCurrentTierSelection = this.updateCurrentTierSelection.bind(this);
    this.updateSelectionStatus = this.updateSelectionStatus.bind(this);
    this.updateTierItems = this.updateTierItems.bind(this);
    this.handleHierarchyCallback = this.handleHierarchyCallback.bind(this);
  }

  render() {
    const { orgTypeStatusMap } = this.state;
    const orgSelectors = this.state.orgState.map((orgState) => {
      const selected = orgState.selectedItems;
      let selectedTerm = selected && selected.length ? selected[0].mainLine : this.getAllOrSelectCaption(this.state, orgState.orgType); // this.props.selectorConfig[orgState.tierName].defaultsTo;
      if (selected.length > 1) {
        selectedTerm = `${selectedTerm} + ${selected.length - 1} more`;
      }
      if (orgState.orgType === OrgTypes.MERCHANT) {
        if (selected.length === 0 && orgState.items.length === 0) {
          selectedTerm = 'Search by MID';
        }
      }
      const enabled = orgState.orgType === OrgTypes.CORP || orgState.orgType === OrgTypes.MERCHANT ? true : this.state.orgState[orgState.orgType - 1].selectedItems.length > 0;
      const onSelect = orgState.orgType < OrgTypes.MERCHANT ? this.onOrgSelected : this.onMerchantSelected;
      const lazyLoader = () => this.onLazyLoad(orgState.orgType);
      const needsInput = (this.props.accordionErrors) ? orgTypeStatusMap[orgState.orgType].required && !orgTypeStatusMap[orgState.orgType].selected : false;
      return (
        <OrgSelectorWidget
          key={orgState.orgType}
          labelText={orgState.tierName.toUpperCase()}
          isGlobalSelector
          dataList={orgState.items}
          enabled={enabled}
          onChange={() => 0}
          handleSelect={(selection) => onSelect(selection, orgState.orgType)}
          selectedTerm={selectedTerm}
          selectedItems={selected}
          isLoading={orgState.isLoading}
          isLocked={this.state.isLocked || (orgState.orgType < OrgTypes.MERCHANT && this.state.isMidSearchLocked)}
          forceCloseOnSelect={orgState.orgType !== OrgTypes.MERCHANT}
          forceCloseOnSelectAll
          onLazyLoad={lazyLoader}
          onLazyLoadSearch={this.onLazySearchByMID}
          enableLazyLoadSearch={orgState.orgType === OrgTypes.MERCHANT && orgState.items.length === 0 && orgState.selectedItems.length === 0}
          lazySearchPlaceholder={counterpart('globalTranslate.reports.enterFullMIDAndPressEnter')}
          needsInput={needsInput}
          placeholder={counterpart(`globalTranslate.common.search${orgState.tierName[0].toUpperCase() + orgState.tierName.slice(1)}`)}
          chainSelected={this.state.orgState[4] && this.state.orgState[4].selectedItems.length > 0}
        />
      );
    });

    return (
      <div className="global-selector-widgets hidden-l" id="global-selector-widgets-mobile">
        {orgSelectors}
      </div>
    );
  }
}

OrgHierarchySelectorRow.propTypes = {
  initialSelections: PropTypes.array.isRequired,
  hierarchySelectionCallback: PropTypes.func.isRequired,
  lookupOrganizations: PropTypes.func.isRequired,
  selectorConfig: PropTypes.object.isRequired,
  rowKey: PropTypes.number.isRequired,
  enableLazyLoading: PropTypes.bool,
  targetRole: PropTypes.string,
  accordionErrors: PropTypes.number.isRequired,
  isGlobalSelector: PropTypes.bool.isRequired,
  getDirectTopLevelOrganizations: PropTypes.func.isRequired,
};

OrgHierarchySelectorRow.defaultProps = {
  initialSelections: [],
  lookupOrganizations: () => {},
  onSelectionChanged: () => 1,
  preloadEmptySelectors: false,
  enableLazyLoading: false,
  targetRole: '',
};

export default OrgHierarchySelectorRow;
